import log from 'loglevel';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { YaSplitInfo } from 'types/page-model';
import { SET_YANDEX_SPLIT_SESSION, RESET_YANDEX_SPLIT_SESSION } from '../reducer';
import { getYandexSplitSession } from '../selectors';

type Args = {
  currency: string;
  isBasketLoading: boolean;
  price?: number | null;
  quantity: number;
  yaSplitInfo: YaSplitInfo;
};

const DEFAULT_BORDER_RADIUS = 8;

export const useYandexPayWidget = ({ currency, isBasketLoading, price, quantity, yaSplitInfo }: Args) => {
  const dispatch = useDispatch();
  const widgetRef = useRef<HTMLDivElement>(null);
  const currentSession = useSelector(getYandexSplitSession);
  quantity = quantity || 1;

  useEffect(() => {
    if (currentSession || !yaSplitInfo || !price || isBasketLoading) return;
    const totalAmount = (price * quantity).toFixed(2);

    async function init() {
      const YaPay = window.YaPay;

      if (!YaPay || !widgetRef.current) return;

      const {
        merchantId,
        env,
        version,
        availablePaymentMethods,
        hideWidgetHeader = true,
        borderRadius = DEFAULT_BORDER_RADIUS,
        withOutline = false,
        widgetSize = YaPay.WidgetSize.Small,
        widgetBackground = YaPay.WidgetBackgroundType.Default,
        widgetTheme = YaPay.WidgetTheme.Light,
        widgetType = YaPay.WidgetType.Ultimate,
        padding = YaPay.WidgetPaddingType.Default,
      } = yaSplitInfo;

      try {
        const session = await YaPay.createSession(
          {
            env: YaPay.PaymentEnv[env],
            version,
            currencyCode: currency,
            merchantId,
            totalAmount,
            availablePaymentMethods,
          },
          { onPayButtonClick: () => {}, onFormOpenError: (error) => log.error(error) },
        );

        widgetRef.current.innerHTML = '';
        session.mountWidget(widgetRef.current, {
          borderRadius,
          hideWidgetHeader,
          padding,
          widgetBackground,
          widgetSize,
          widgetTheme,
          widgetType,
          withOutline,
        });

        dispatch(SET_YANDEX_SPLIT_SESSION(session));
      } catch (error) {
        dispatch(RESET_YANDEX_SPLIT_SESSION());
      }
    }

    init();
  }, [currency, currentSession, dispatch, isBasketLoading, price, quantity, widgetRef, yaSplitInfo]);

  return widgetRef;
};
